import React from 'react';
import './CustomerLoanReport.css';
import TopToolbar from '../../global/subcomponents/topToolbar/TopToolbar';
import FeedbackBar from '../../feedbackBar/FeedbackBar';
import withShipment from '../../../withShipment';
import { userInfoSelector } from '../../../redux/selectors/auth';
import { loadingSelector } from '../../../redux/selectors/settings';
import { customersSelector } from '../../../redux/selectors/customer';
import { listAllCustomers } from '../../../redux/actions/customer';

import TextField from '@material-ui/core/TextField';
import Autocomplete from '@material-ui/lab/Autocomplete';

import {
  KeyboardDatePicker,
  MuiPickersUtilsProvider,
} from '@material-ui/pickers';
import DateFnsUtils from '@date-io/date-fns';
import InputAdornment from '@material-ui/core/InputAdornment';
import { customerLoanSelector } from '../../../redux/selectors/loan';
import {
  listCustomerLoan,
  saveCustomerLoan,
} from '../../../redux/actions/loan';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';

import {
  CardContent,
  Typography,
  IconButton,
  Collapse,
  Divider,
  List,
  ListItem,
  ListItemText,
  Tooltip,
} from '@mui/material';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { BACK } from '../../customer/constants';
import KeyboardBackspaceIcon from '@material-ui/icons/KeyboardBackspace';
import { GetApp } from '@material-ui/icons';
import VisibilityIcon from '@mui/icons-material/Visibility';
import Utils from '../../Utils/Utils';
import WriteGuard from '../../global/ReadOnly/WriteGuard';

class CustomerLoanReport extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      feedbackBarOpen: false,
      feedbackMessage: '',
      feedbackStatus: '',
      selectedCustomerId: null,
      amountInput: '',
      dateInput: new Date().toISOString().split('T')[0],
      loanHistory: {},
      displayOrder: [],
      expanded: {},
      selectedType: null,
      balanceFilter: 'UNPAID',
      viewingReportFor: null,
    };
    this.colorMap = {};
  }

  componentDidMount() {
    this.props.listAllCustomers({
      locationId: this.props.userInfo.selectedLocationId,
    });
    this.props.listCustomerLoan({
      locationId: this.props.userInfo.selectedLocationId,
    });
  }

  componentDidUpdate(prevProps) {
    if (prevProps.customerLoanHistory !== this.props.customerLoanHistory) {
      const loanHistoryObj = {};

      this.props.customerLoanHistory.forEach((loan) => {
        loanHistoryObj[loan.customerLoanId] = (loan.loanEntries || []).map(
          (entry) => ({
            ...entry,
            transactionId: entry.transactionId || null,
          })
        );
      });

      this.setState({
        loanHistory: loanHistoryObj,
      });
    }
  }

  handleDownloadPDF = (customer) => {
    const doc = new jsPDF();

    // 1. Sort history oldest to newest
    const history = [
      ...(this.state.loanHistory[customer.customerId] || []),
    ].sort((a, b) => new Date(a.date) - new Date(b.date));

    // 2. Add Header Text
    doc.setFontSize(22);
    doc.setTextColor(51, 51, 51);
    doc.text(`${customer.firstName} ${customer.lastName}`, 14, 20);

    doc.setFontSize(12);
    doc.setTextColor(102, 102, 102);

    // 3. Map Data & Calculate Running Balance
    let runningBalance = 0;
    const tableRows = history.map((row) => {
      runningBalance += row.amount;

      // Get day of the week
      const day = new Date(row.date.replace(/-/g, '/')).toLocaleDateString(
        'en-US',
        {
          weekday: 'long',
          timeZone: 'America/Chicago', // Explicitly set the timezone
        }
      );

      return [
        row.date,
        day,
        `${row.amount < 0 ? 'Payment' : 'Loan'} - ${
          row.employeeName?.toUpperCase() || ''
        }`,
        row.amount < 0
          ? `-$${Math.abs(row.amount).toLocaleString()}`
          : `+$${row.amount.toLocaleString()}`,
        `$${runningBalance.toLocaleString()}`,
      ];
    });

    // 4. Generate Auto-Table
    autoTable(doc, {
      startY: 30,
      head: [['Date', 'Day', 'Description', 'Amount', 'Balance']],
      body: tableRows,
      theme: 'grid',
      headStyles: {
        fillColor: [255, 240, 245], // Pink header
        textColor: [0, 0, 0],
        fontSize: 12,
        fontStyle: 'bold',
      },
      styles: {
        fontSize: 10,
        cellPadding: 5,
      },
      columnStyles: {
        3: { halign: 'right', fontStyle: 'bold' },
        4: { halign: 'right', fontStyle: 'bold' },
      },
      // --- ROW COLOR LOGIC ---
      didParseCell: (data) => {
        // Check if we are in a body row (not the header)
        if (data.section === 'body') {
          const rowData = history[data.row.index];
          if (rowData.amount < 0) {
            // Light Red for Payments
            data.cell.styles.fillColor = [255, 235, 235];
            // Set Amount column text to Dark Red
            if (data.column.index === 3)
              data.cell.styles.textColor = [183, 28, 28];
          } else {
            // Light Green for Loans
            data.cell.styles.fillColor = [232, 245, 233];
            // Set Amount column text to Dark Green
            if (data.column.index === 3)
              data.cell.styles.textColor = [46, 125, 50];
          }
        }
      },
      margin: { top: 20 },
    });

    // 5. Add "Total Owed" at the very end
    const finalY = doc.lastAutoTable.finalY + 15;
    doc.setFontSize(14);
    doc.setTextColor(211, 47, 47);
    const totalStr = `Total owed: $${this.getCustomerBalance(
      customer.customerId
    ).toLocaleString(undefined, { minimumFractionDigits: 2 })}`;

    // Align to the right
    const pageSize = doc.internal.pageSize;
    const pageWidth = pageSize.width ? pageSize.width : pageSize.getWidth();
    doc.text(totalStr, pageWidth - 14, finalY, { align: 'right' });

    // 6. Output to bridge
    const pdfData = doc.output('arraybuffer');

    Utils.handlePdfAction(
      pdfData,
      'download',
      `Customer_Loan_Report_${customer.lastName}.pdf`
    );
  };

  renderDetailView(custId) {
    const customer = this.props.customers.find((c) => c.customerId === custId);
    if (!customer) return null;

    // Sorting oldest to newest for chronological balance logic
    const history = [...(this.state.loanHistory[custId] || [])].sort(
      (a, b) => new Date(a.date) - new Date(b.date)
    );

    let runningBalance = 0;
    const displayRows = history.map((t) => {
      runningBalance += t.amount;
      return { ...t, currentBalance: runningBalance };
    });

    const totalBalance = this.getCustomerBalance(custId);

    return (
      <div className="report-detail-container">
        {/* Added id for precise capture target */}
        <div id="capture-report-area" className="report-pink-card">
          <div className="report-card-header">
            <Typography className="report-customer-name">
              {customer.firstName} {customer.lastName}
            </Typography>
          </div>

          <div className="table-responsive-wrapper">
            <table className="loan-report-table">
              <thead>
                <tr>
                  <th>Date</th>
                  <th>Day</th>
                  <th>Description</th>
                  <th>Amount</th>
                  <th>Balance</th>
                </tr>
              </thead>
              <tbody>
                {displayRows.map((row, index) => {
                  const day = new Date(
                    row.date.replace(/-/g, '/')
                  ).toLocaleDateString('en-US', {
                    weekday: 'long',
                    timeZone: 'America/Chicago', // Explicitly set the timezone
                  });
                  return (
                    <tr
                      key={index}
                      className={index % 2 === 0 ? 'row-even' : 'row-odd'}
                    >
                      <td className="date-cell">{row.date}</td>
                      <td className="day-cell">{day}</td>
                      <td className="desc-cell">
                        {row.amount < 0 ? 'Payment' : 'Loan'} –{' '}
                        {row.employeeName?.toUpperCase()}
                      </td>
                      <td
                        className={`amount-cell ${
                          row.amount < 0 ? 'text-red' : 'text-green'
                        }`}
                      >
                        {row.amount < 0
                          ? `- $${Math.abs(row.amount).toLocaleString()}`
                          : `+ $${row.amount.toLocaleString()}`}
                      </td>
                      <td className="balance-cell">
                        $
                        {row.currentBalance.toLocaleString(undefined, {
                          minimumFractionDigits: 2,
                        })}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          <div className="report-card-footer">
            <Typography className="footer-label">Total owed:</Typography>
            <Typography className="footer-amount">
              $
              {totalBalance.toLocaleString(undefined, {
                minimumFractionDigits: 2,
              })}
            </Typography>
          </div>
        </div>
      </div>
    );
  }

  toggleExpand = (custId) => {
    this.setState((prev) => ({
      expanded: {
        ...prev.expanded,
        [custId]: !prev.expanded[custId],
      },
    }));
  };

  getColorForCustomer(custId) {
    if (!this.colorMap[custId]) {
      this.colorMap[custId] = this.getRandomLightColor();
    }
    return this.colorMap[custId];
  }

  openFeedbackBar = (status, message) => {
    this.setState({
      feedbackStatus: status,
      feedbackMessage: message,
      feedbackBarOpen: true,
    });
  };

  closeFeedbackBar = () => {
    this.setState({
      feedbackBarOpen: false,
      feedbackStatus: '',
      feedbackMessage: '',
    });
  };

  handleCustomerChange = (event, newValue) => {
    // newValue is the selected customer object or null
    this.setState({
      selectedCustomerId: newValue ? newValue.customerId : null,
    });
  };

  handleDateChange = (date) => {
    if (date && date.toString() !== 'Invalid Date') {
      // format to yyyy-MM-dd string for your existing code logic (optional)
      const formattedDate = date.toISOString().split('T')[0];
      this.setState({ dateInput: formattedDate });
    } else {
      this.setState({ dateInput: '' });
    }
  };

  handleAddTransaction = () => {
    const {
      amountInput,
      dateInput,
      selectedCustomerId,
      loanHistory,
      selectedType,
    } = this.state;

    if (!selectedCustomerId) {
      this.openFeedbackBar('error', 'Please select a customer');
      return;
    }

    if (!selectedType) {
      this.openFeedbackBar('error', 'Please select a type');
      return;
    }

    if (!amountInput || isNaN(amountInput) || parseFloat(amountInput) <= 0) {
      this.openFeedbackBar(
        'error',
        'Please enter a valid amount greater than 0'
      );
      return;
    }

    if (!dateInput) {
      this.openFeedbackBar('error', 'Please select a date');
      return;
    }

    const customer = this.props.customers.find(
      (c) => c.customerId === selectedCustomerId
    );

    // Prepare the new transaction
    let amount = parseFloat(amountInput);
    if (selectedType.toUpperCase() === 'SUBTRACT') {
      amount = -amount;
    }

    const newTransaction = {
      firstName: customer.firstName,
      lastName: customer.lastName,
      amount,
      date: dateInput,
      type: selectedType,
      employeeName: this.props.userInfo?.firstname,
    };

    // Add new transaction to shortHistory
    const updatedHistory = {
      ...loanHistory,
      [selectedCustomerId]: [
        newTransaction,
        ...(loanHistory[selectedCustomerId] || []),
      ],
    };

    // Build new display order
    const allCustomerIds = this.props.customers.map((c) => c.customerId);
    const newDisplayOrder = [
      selectedCustomerId,
      ...allCustomerIds.filter((id) => id !== selectedCustomerId),
    ];

    // Call backend with updatedHistory
    const callback = (error, response) => {
      if (error) {
        this.openFeedbackBar('error', error);
      } else {
        this.setState({
          loanHistory: updatedHistory,
          displayOrder: newDisplayOrder,
          amountInput: '',
          dateInput: new Date().toISOString().split('T')[0],
        });

        this.openFeedbackBar('success', response);
      }
    };

    this.props.saveCustomerLoan(
      {
        loanHistory: updatedHistory,
        locationId: this.props.userInfo.selectedLocationId,
      },
      callback
    );
  };

  getCustomerBalance = (customerId) => {
    const history = this.state.loanHistory[customerId] || [];
    return history.reduce((acc, t) => acc + t.amount, 0);
  };

  getRandomLightColor() {
    const r = Math.floor(Math.random() * 25) + 230; // 230-255
    const g = Math.floor(Math.random() * 25) + 230;
    const b = Math.floor(Math.random() * 25) + 230;
    return `rgb(${r}, ${g}, ${b})`;
  }

  userIsAnAdmin = () => {
    const { userInfo } = this.props;
    const roles = userInfo?.authority || [];
    return roles.includes('ROLE_ADMIN') || roles.includes('ROLE_MASTER_ADMIN');
  };

  render() {
    const { selectedCustomerId, dateInput, loanHistory, viewingReportFor } =
      this.state;

    const currentViewCustomer = viewingReportFor
      ? this.props.customers.find((c) => c.customerId === viewingReportFor)
      : null;

    const menuItems = viewingReportFor
      ? [
          {
            title: BACK,
            icon: <KeyboardBackspaceIcon />,
            onClick: () => this.setState({ viewingReportFor: null }),
          },
          {
            title: 'Download',
            icon: <GetApp />,
            onClick: () => this.handleDownloadPDF(currentViewCustomer),
          },
        ]
      : [];

    const orderedCustomers = this.props.customers
      .filter((c) => (loanHistory[c.customerId] || []).length > 0) // only customers with loan entries
      .filter((c) => {
        const balance = this.getCustomerBalance(c.customerId);
        if (this.state.balanceFilter === 'PAID') return balance === 0;
        return balance !== 0; // UNPAID
      })
      .sort((a, b) => {
        const aEntries = loanHistory[a.customerId];
        const bEntries = loanHistory[b.customerId];

        const aDate =
          aEntries && aEntries.length > 0
            ? new Date(aEntries[0].date)
            : new Date(0);
        const bDate =
          bEntries && bEntries.length > 0
            ? new Date(bEntries[0].date)
            : new Date(0);

        return bDate - aDate; // latest first
      })
      .map((c) => c.customerId);

    return (
      <React.Fragment>
        <TopToolbar
          pageName={
            viewingReportFor ? 'Customer Loan Report' : 'Customer Loan List'
          }
          menuItems={menuItems}
        />

        <div className="loan-container">
          {viewingReportFor ? (
            this.renderDetailView(viewingReportFor)
          ) : (
            <>
              {/* Add-transaction form. Greyed and inert at a VIEW-ONLY
                  location — the report itself stays fully readable, only
                  recording a new entry is off.
                  See constants/readOnlyAccess.js. */}
              {this.userIsAnAdmin() && (
                <WriteGuard mode="disable" noTooltip>
                <div className="loan-form">
                  <div className="form-row">
                    <Autocomplete
                      className="form-input"
                      options={this.props.customers}
                      getOptionLabel={(option) =>
                        `${option.firstName} ${option.lastName}`
                      }
                      value={
                        selectedCustomerId
                          ? this.props.customers.find(
                              (c) => c.customerId === selectedCustomerId
                            ) || null
                          : null
                      }
                      onChange={this.handleCustomerChange}
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          required
                          name="customer"
                          label="Select Customer"
                          variant="outlined"
                          InputLabelProps={{ shrink: true }}
                        />
                      )}
                      filterOptions={(options, { inputValue }) => {
                        return options.filter(
                          (c) =>
                            c.firstName
                              .toLowerCase()
                              .includes(inputValue.toLowerCase()) ||
                            c.lastName
                              .toLowerCase()
                              .includes(inputValue.toLowerCase())
                        );
                      }}
                      clearOnEscape
                    />

                    <Autocomplete
                      className="form-input"
                      options={['ADD', 'SUBTRACT']}
                      value={this.state.selectedType || null}
                      onChange={(event, newValue) => {
                        this.setState({ selectedType: newValue });
                      }}
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          required
                          name="type"
                          label="Select Type"
                          variant="outlined"
                          InputLabelProps={{ shrink: true }}
                        />
                      )}
                      clearOnEscape
                    />

                    <TextField
                      className="form-input"
                      variant="outlined"
                      label="Amount"
                      InputLabelProps={{ shrink: true }}
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">$</InputAdornment>
                        ),
                      }}
                      name="amount"
                      required
                      value={this.state.amountInput}
                      onChange={(e) => {
                        // Only allow digits and optional decimal point
                        const value = e.target.value;
                        const sanitizedValue = value.replace(/[^0-9.]/g, '');
                        this.setState({ amountInput: sanitizedValue });
                      }}
                      type="text" // keep as text to fully control input
                      onBlur={() => {
                        let val = parseFloat(this.state.amountInput);
                        if (isNaN(val) || val < 0) val = 0;
                        this.setState({ amountInput: val.toFixed(2) });
                      }}
                    />

                    <MuiPickersUtilsProvider utils={DateFnsUtils}>
                      <KeyboardDatePicker
                        className="form-input"
                        autoOk
                        name="toDate"
                        variant="inline"
                        inputVariant="outlined"
                        label="Date"
                        format="yyyy-MM-dd"
                        required
                        InputAdornmentProps={{ position: 'start' }}
                        value={dateInput ? new Date(dateInput) : null}
                        onChange={this.handleDateChange}
                      />
                    </MuiPickersUtilsProvider>

                    <button
                      onClick={this.handleAddTransaction}
                      className="loan-btn"
                    >
                      Add
                    </button>
                  </div>
                </div>
                </WriteGuard>
              )}

              <div className="balance-filter">
                <label>
                  <input
                    type="radio"
                    name="balanceFilter"
                    value="UNPAID"
                    checked={this.state.balanceFilter === 'UNPAID'}
                    onChange={(e) =>
                      this.setState({ balanceFilter: e.target.value })
                    }
                  />
                  Unpaid
                </label>
                <label style={{ marginLeft: '20px' }}>
                  <input
                    type="radio"
                    name="balanceFilter"
                    value="PAID"
                    checked={this.state.balanceFilter === 'PAID'}
                    onChange={(e) =>
                      this.setState({ balanceFilter: e.target.value })
                    }
                  />
                  Paid
                </label>
              </div>

              {/* All Customers Loan History */}

              {(!this.props.customerLoanHistory ||
                this.props.customerLoanHistory.length === 0) && (
                <div className="no-history-container">
                  <CardContent className="no-history-card">
                    <div className="no-history-icon">
                      <span role="img" aria-label="empty">
                        📄
                      </span>
                    </div>
                    <Typography variant="h6" className="no-history-title">
                      No Customer Loan Added Yet
                    </Typography>
                    <span className="no-history-subtitle">
                      Once you add a record, it will appear here.
                    </span>
                  </CardContent>
                </div>
              )}

              <div className="loan-history-card-container">
                {orderedCustomers.map((custId) => {
                  const cust = this.props.customers.find(
                    (c) => c.customerId === custId
                  );
                  if (!cust) return null;

                  const history = loanHistory[custId] || [];
                  if (history.length === 0) return null;

                  const balance = this.getCustomerBalance(custId);
                  const isExpanded = this.state.expanded[custId] || false;

                  return (
                    <div
                      key={custId}
                      className="note-card-loan"
                      style={{
                        backgroundColor: this.getColorForCustomer(custId),
                      }}
                    >
                      {/* Customer Name */}
                      <CardContent className="">
                        <div
                          style={{
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'center',
                          }}
                        >
                          <Typography variant="h6" className="customer-name">
                            {cust.firstName} {cust.lastName}
                          </Typography>

                          <div>
                            <Tooltip title="View Detailed Report">
                              <IconButton
                                onClick={() =>
                                  this.setState({ viewingReportFor: custId })
                                }
                                size="small"
                              >
                                <VisibilityIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                            <IconButton
                              onClick={() => this.toggleExpand(custId)}
                              size="small"
                            >
                              {isExpanded ? (
                                <ExpandLessIcon />
                              ) : (
                                <ExpandMoreIcon />
                              )}
                            </IconButton>
                          </div>
                        </div>
                      </CardContent>

                      <Divider className="thin-divider" />

                      {/* Transactions List */}
                      <Collapse in={isExpanded} timeout="auto" unmountOnExit>
                        <CardContent className="transactions-list">
                          <List dense>
                            {history.map((t, i) => (
                              <ListItem
                                key={i}
                                className={
                                  t.amount < 0
                                    ? 'amount-negative'
                                    : 'amount-positive'
                                }
                              >
                                <ListItemText
                                  primary={
                                    <>
                                      <span
                                        style={{
                                          fontSize: '16px',
                                          fontWeight: 'bold',
                                        }}
                                      >
                                        {`${
                                          t.amount < 0 ? '-' : '+'
                                        } $${Math.abs(t.amount)}`}
                                      </span>
                                    </>
                                  }
                                  secondary={
                                    <>
                                      {t.date} —{' '}
                                      <span style={{ fontStyle: 'italic' }}>
                                        {t.employeeName}
                                      </span>
                                    </>
                                  }
                                />
                              </ListItem>
                            ))}
                          </List>
                        </CardContent>
                      </Collapse>

                      {/* Balance */}
                      <CardContent className="balance-section">
                        <Typography
                          variant="h6"
                          style={{ fontSize: '16px', fontWeight: 'bold' }}
                          className={
                            balance < 0 ? 'amount-negative' : 'amount-positive'
                          }
                        >
                          Balance{': '}
                          {balance < 0
                            ? `- $${Math.abs(balance).toFixed(2)}`
                            : `$${balance.toFixed(2)}`}
                        </Typography>
                      </CardContent>
                    </div>
                  );
                })}
              </div>
            </>
          )}
        </div>

        <FeedbackBar
          open={this.state.feedbackBarOpen}
          handleClose={this.closeFeedbackBar}
          severity={this.state.feedbackStatus}
          message={this.state.feedbackMessage}
        />
      </React.Fragment>
    );
  }
}

const mapStateToProps = (state) => ({
  userInfo: userInfoSelector(state),
  loadingData: loadingSelector(state),
  customers: customersSelector(state),
  customerLoanHistory: customerLoanSelector(state),
});

const actionCreators = {
  listAllCustomers,
  saveCustomerLoan,
  listCustomerLoan,
};

export default withShipment(
  { actionCreators, mapStateToProps },
  CustomerLoanReport
);
